import * as React from 'react';
import {
  Building2,
  CarFront,
  Handshake,
  KeyRound,
  Layers,
  PackageOpen,
  Settings2,
  Tags,
  Truck,
  Users,
} from 'lucide-react';
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { useIsFeatureFlagActive, usePermissions } from '@klareospace/pharos-lib-front-utils';
import { PageHeader, RouteTabs } from '@klareospace/pharos-lib-web-utils';
import type { RouteTab } from '@klareospace/pharos-lib-web-utils';
import { FEATURE_FLAGS } from '&/feature-flags';
import { SETTINGS_ACCESS_PERMISSIONS } from '&/features/settings/shared/settings-navigation';
import { MainSelector } from '&/features/main-selector';
import { useSettingsScopeRedirect } from '&/hooks/use-settings-scope-redirect';

// ─── Route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/_authenticated/settings')({
  beforeLoad: ({ context }): void => {
    const canAccessSettings = context.permissions.hasAnyPermission([...SETTINGS_ACCESS_PERMISSIONS]);
    if (context.permissions.isReady && !canAccessSettings) {
      throw redirect({ to: '/' });
    }
  },
  component: SettingsLayout,
});

// ─── Layout ───────────────────────────────────────────────────────────────────

function SettingsLayout(): React.JSX.Element {
  const { t } = useTranslation('settings');
  const { hasAnyPermission, hasPermission } = usePermissions();
  const isServiceKeysEnabled = useIsFeatureFlagActive(FEATURE_FLAGS.SERVICE_KEYS_ENABLED);

  // Delegate all scope-driven redirect logic to the shared hook.
  // includeAccessFallback: true mirrors the cold-load-race home redirect
  // that previously lived inline in this component's effect.
  useSettingsScopeRedirect({ includeAccessFallback: true });

  const tabs: RouteTab[] = [
    {
      anyPermissions: ['tenants:manage', 'tenants:access-read-only'],
      icon: <Building2 className="h-4 w-4" />,
      label: t('settings.tabs.organization'),
      to: '/settings/organization',
    },
    {
      anyPermissions: ['users:manage', 'users:access-read-only'],
      icon: <Users className="h-4 w-4" />,
      label: t('settings.tabs.users'),
      to: '/settings/users',
    },
    {
      anyPermissions: ['tenants:manage', 'tenants:access-read-only'],
      icon: <Layers className="h-4 w-4" />,
      label: t('settings.tabs.spaces'),
      to: '/settings/spaces',
    },
    {
      anyPermissions: ['tenants:manage', 'tenants:access-read-only'],
      icon: <PackageOpen className="h-4 w-4" />,
      label: t('settings.tabs.handlingUnits'),
      to: '/settings/handling-units',
    },
    {
      anyPermissions: ['tenants:manage', 'tenants:access-read-only'],
      icon: <Tags className="h-4 w-4" />,
      label: t('settings.tabs.orderTypes'),
      to: '/settings/order-types',
    },
    {
      anyPermissions: ['tenants:manage', 'tenants:access-read-only'],
      icon: <CarFront className="h-4 w-4" />,
      label: t('settings.tabs.vehicleTemplates'),
      to: '/settings/vehicle-templates',
    },
    {
      anyPermissions: ['tenants:manage', 'tenants:access-read-only'],
      icon: <Truck className="h-4 w-4" />,
      label: t('settings.tabs.nationalCarriers'),
      to: '/settings/national-carriers',
    },
    {
      anyPermissions: ['tenants:manage', 'tenants:access-read-only'],
      icon: <Handshake className="h-4 w-4" />,
      label: t('settings.tabs.shippers'),
      to: '/settings/shippers',
    },
    ...(isServiceKeysEnabled
      ? [
          {
            anyPermissions: ['servicekeys:manage'],
            icon: <KeyRound className="h-4 w-4" />,
            label: t('settings.tabs.serviceKeys'),
            to: '/settings/service-keys',
          } satisfies RouteTab,
        ]
      : []),
    {
      anyPermissions: ['tenants:manage', 'tenants:access-read-only'],
      icon: <Settings2 className="h-4 w-4" />,
      label: t('settings.tabs.defaults'),
      to: '/settings/defaults',
    },
  ];

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <PageHeader breadcrumb={<MainSelector />} title={t('settings.title')} variant="banner" />
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="px-6 pb-3 pt-4">
          <RouteTabs
            ariaLabel={t('settings.title')}
            hasAnyPermission={hasAnyPermission}
            hasPermission={hasPermission}
            size="sm"
            tabs={tabs}
          />
        </div>
        <div className="flex flex-1 flex-col overflow-hidden px-6 pb-6 pt-1">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
